import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RichText from "./RichText";
import { BarChart3, Plus, Trash2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import AdvancedChart from "./AdvancedChart";
import TickerSearch from "./TickerSearch";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";

/**
 * Charts inside an investor's post.
 *
 * The rule the whole file exists for: **a published chart is frozen.** The author
 * picks the stock, the window and the drawings; all three are captured with the price
 * series itself and stored on the post. A reader gets a picture, not a widget — no
 * brush, no drawing tools, no zoom. If the marked block could move, the sentence
 * pointing at it would stop being true, and the post would quietly rot as the market
 * moved on.
 *
 * Freezing the series has a second benefit: a three-year-old post about a delisted
 * ticker still renders, because nothing is fetched to display it.
 */

const PERIODS = ["3mo", "6mo", "1y", "2y", "5y"];
// Matches MAX_POINTS in backend/post_media.py. Kept in step by hand rather than
// fetched: it changes roughly never, and a chart that silently loses its tail because
// the two drifted would be a miserable bug to find.
const MAX_POINTS = 400;

/** Read-only figure. Takes no callbacks, which is what makes it unchangeable. */
export function PostChart({ chart }) {
  const { t } = useTranslation();
  if (!chart?.series?.length) return null;
  const data = chart.series.map((p) => ({
    date: p.d, close: p.close, high: p.high ?? p.close, low: p.low ?? p.close,
  }));
  return (
    <figure className="card p-4 sm:p-5 my-5" data-testid={`post-chart-${chart.ticker}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 size={16} className="text-brand-500 shrink-0" />
          <Link to={`/instrument/${chart.ticker}`}
                className="font-heading font-semibold tracking-tight hover:text-brand-600 truncate">
            {chart.ticker}
          </Link>
          {chart.name && <span className="text-xs text-slate-400 truncate hidden sm:inline">{chart.name}</span>}
        </div>
        <span className="text-[11px] text-slate-400 flex items-center gap-1 shrink-0" title={t("postCharts.frozenHint")}>
          <Lock size={11} /> {t("postCharts.asPublished", { date: shortDate(chart.captured_at) })}
        </span>
      </div>
      <AdvancedChart data={data} readOnly shapes={chart.shapes || []}
                     range={{ start: 0, end: data.length - 1 }} />
      {chart.caption && (
        <figcaption className="mt-3">
          <RichText html={chart.caption} className="text-sm text-slate-600" />
        </figcaption>
      )}
    </figure>
  );
}

export function PostChartList({ charts }) {
  if (!charts?.length) return null;
  return <>{charts.map((c, i) => <PostChart key={`${c.ticker}-${i}`} chart={c} />)}</>;
}

/**
 * Author-side: pick a stock, frame it, draw on it, keep it. Produces exactly one
 * chart and hands it back — where it lands is the caller's business.
 *
 * This was `ChartBuilder`, which owned a list of charts and appended to it. Once a
 * post became an ordered list of blocks, "the charts" stopped being a collection with
 * a place of its own: a chart is a block, sitting wherever the author put it. So the
 * composer produces one and the block list owns the order.
 *
 * `presetTicker` skips straight to loading that stock. It is what makes "add the chart
 * for the stock this post is about" a single tap rather than a search.
 */
export function ChartComposer({ presetTicker = null, onKeep, onCancel }) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(!presetTicker);
  const [draft, setDraft] = useState(null);   // { ticker, name, period, data, shapes, range }
  const [loading, setLoading] = useState(false);

  const loadSeries = async (ticker, name, period) => {
    setLoading(true);
    try {
      const { data } = await api.history(ticker, period, "1d");
      const rows = (data?.data || []).map((p) => ({
        date: p.date || p.d, close: p.close, high: p.high ?? p.close, low: p.low ?? p.close,
      })).filter((p) => p.date && p.close != null);
      if (!rows.length) { toast.error(t("postCharts.noData")); return; }
      // Keep the most recent points when a long period overshoots the cap — the tail
      // is the part anyone is writing about.
      const trimmed = rows.slice(-MAX_POINTS);
      setDraft((prev) => {
        // Switching period must not throw away work. This used to reset shapes to []
        // and the caption to "", so drawing a trend line, writing the caption and then
        // reaching for 6mo silently discarded both — the reported "the chart keeps
        // resetting and doesn't keep what I enter".
        //
        // Shapes survive by construction: their x coordinates are *dates* on the
        // category axis (see backend/post_media.py `_date`), not indices into this
        // array, so a mark stays attached to the day it was made whatever window is
        // loaded around it. Only `range` has to be rebuilt, because it indexes the
        // series that was just replaced.
        const same = prev && prev.ticker === ticker;
        return {
          ticker,
          name: name || (same ? prev.name : ""),
          period,
          data: trimmed,
          shapes: same ? (prev.shapes || []) : [],
          caption: same ? (prev.caption || "") : "",
          range: { start: 0, end: trimmed.length - 1 },
        };
      });
      setPicking(false);
    } catch { toast.error(t("postCharts.noData")); }
    finally { setLoading(false); }
  };

  // A preset ticker means the author already told us what the post is about; asking
  // them to search for it again would be asking a question we know the answer to.
  useEffect(() => {
    if (presetTicker) loadSeries(presetTicker, "", "1y");
    // `loadSeries` is intentionally not a dependency: it is recreated on every render,
    // and depending on it would reload the series (discarding the author's drawings)
    // on every keystroke in the caption.
  }, [presetTicker]);   // eslint-disable-line

  const keep = () => {
    if (!draft) return;
    onKeep({
      ticker: draft.ticker,
      name: draft.name,
      period: draft.period,
      interval: "1d",
      caption: draft.caption,
      shapes: draft.shapes,
      range: draft.range,
      // Only the framed window is stored. The rest was never part of the argument,
      // and it is half the payload.
      series: draft.data.slice(draft.range.start, draft.range.end + 1)
        .map((p) => ({ d: p.date, close: p.close, high: p.high, low: p.low })),
    });
    setDraft(null);
  };

  return (
    <div className="space-y-3" data-testid="chart-composer">
      {picking && (
        <div className="rounded-xl border border-slate-200 p-3.5 space-y-2">
          <p className="text-xs text-slate-500">{t("postCharts.pickStock")}</p>
          {loading ? <Loader2 className="animate-spin text-brand-500" size={18} />
            : <TickerSearch onSelect={(r) => loadSeries(r.ticker || r.symbol, r.name, "1y")} />}
          <button type="button" className="btn-ghost text-sm" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        </div>
      )}

      {draft && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3.5 space-y-3" data-testid="chart-draft">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="font-heading font-semibold">{draft.ticker}</span>
            <div className="inline-flex bg-white rounded-lg p-0.5 border border-slate-200">
              {PERIODS.map((p) => (
                <button key={p} type="button" data-testid={`chart-period-${p}`}
                        onClick={() => loadSeries(draft.ticker, draft.name, p)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md ${draft.period === p ? "bg-brand-500 text-white" : "text-slate-500"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-500">{t("postCharts.drawHint")}</p>
          <div className="bg-white rounded-xl p-3">
            <AdvancedChart
              data={draft.data}
              shapes={draft.shapes}
              onShapesChange={(s) => setDraft((d) => ({ ...d, shapes: s }))}
              range={draft.range}
              onRangeChange={(r) => setDraft((d) => ({ ...d, range: r }))}
            />
          </div>

          <div>
            <label className="label">{t("postCharts.caption")}</label>
            <textarea className="input mt-1 min-h-[70px] resize-y text-sm" data-testid="chart-caption"
                      value={draft.caption} maxLength={600}
                      placeholder={t("postCharts.captionPlaceholder")}
                      onChange={(e) => setDraft((d) => ({ ...d, caption: e.target.value }))} />
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button type="button" className="btn-ghost text-sm" onClick={() => { setDraft(null); onCancel?.(); }}>
              {t("common.cancel")}
            </button>
            <button type="button" className="btn-primary text-sm" data-testid="keep-chart" onClick={keep}>
              {t("postCharts.keep")}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">{t("postCharts.freezeNotice")}</p>
        </div>
      )}
    </div>
  );
}

/** Stock tags. Structured, so they link through and can be searched on later. */
export function TickerTags({ tickers, onChange }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const editable = typeof onChange === "function";
  if (!tickers?.length && !editable) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ticker-tags">
      {(tickers || []).map((sym) => (
        editable ? (
          <button key={sym} type="button" data-testid={`untag-${sym}`}
                  onClick={() => onChange(tickers.filter((s) => s !== sym))}
                  className="pill bg-brand-50 text-brand-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
            ${sym} ×
          </button>
        ) : (
          <Link key={sym} to={`/instrument/${sym}`} data-testid={`tag-${sym}`}
                className="pill bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors">
            ${sym}
          </Link>
        )
      ))}
      {editable && !adding && (
        <button type="button" className="pill border border-dashed border-slate-300 text-slate-500 hover:border-brand-400"
                data-testid="add-ticker-tag" onClick={() => setAdding(true)}>
          <Plus size={11} /> {t("postCharts.tagStock")}
        </button>
      )}
      {editable && adding && (
        <div className="w-full">
          <TickerSearch onSelect={(r) => {
            const sym = (r.ticker || r.symbol || "").toUpperCase();
            if (sym && !tickers.includes(sym)) onChange([...(tickers || []), sym]);
            setAdding(false);
          }} />
          <button type="button" className="btn-ghost text-xs mt-1" onClick={() => setAdding(false)}>
            {t("common.cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Renders the stock tags on a post the reader is looking at. */
export function PostTickerBar({ tickers }) {
  if (!tickers?.length) return null;
  return <TickerTags tickers={tickers} />;
}

export default PostChartList;
